import React, { useMemo } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
  type ChartOptions,
} from "chart.js";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

export default function AIAdoptation({adoptationLevel , aiPatterns}:any) {
  
  const stackedData = useMemo(() => {
  if (!adoptationLevel) {
    return {
      labels: [],
      datasets: [],
    };
  }

  // enforce consistent label order
  const labelOrder = ["hcp", "patients", "caregivers"];
  const labelNames: Record<string, string> = {
    hcp: "HCPs",
    patients: "Patients",
    caregivers: "Caregivers",
  };

  const colors: Record<string, string> = {
    high: "#9ADC54",
    medium: "#3d5f1f",
    low: "#87C8FF",
  };

  const metrics = ["high", "medium", "low"] as const;

  return {
    labels: labelOrder.map((key) => labelNames[key]),

    datasets: metrics.map((metric) => ({
      label: metric.charAt(0).toUpperCase() + metric.slice(1),
      data: labelOrder.map((segment) =>
        adoptationLevel?.[segment]?.[metric] ?? 0
      ),
      backgroundColor: colors[metric],
      borderRadius: 14,
    })),
  };
}, [adoptationLevel]);


  const stackedOptions: ChartOptions<"bar"> = {
    responsive: true,
    maintainAspectRatio: false,

    animation: {
      duration: 1200,
      easing: "easeOutQuart",
    },

    plugins: {
      legend: {
        position: "top",
        labels: {
          usePointStyle: true,
          pointStyle: "circle",
          padding: 20,
        },
      },
      tooltip: {
        enabled: true,
      },
    },

    scales: {
      x: {
        stacked: true,
        grid: { display: false },
        ticks: {
          font: { size: 14 },
        },
      },
      y: {
        stacked: true,
        min: 0,
        max: 100,
        ticks: { stepSize: 20 },
      },
    },
  };

  const formatKey = (key: string) => {
    // Map backend keys to human-friendly titles
    const keyMap: Record<string, string> = {
      patients: "Patients",
      caregivers: "Caregivers",
      oncology_hcps: "Oncology HCPs",
    };
    return keyMap[key] || key;
  };

  return (
    <div className="flex flex-col h-full items-center">
      <h1 className="relative inline-block text-white text-center font-extrabold 
  max-w-4xl text-2xl lg:text-4xl font-nunito mb-4 
  bg-gradient-to-r from-[#92D050] to-[#8CC54F] 
  px-6 py-2
  before:content-[''] before:absolute before:left-0 before:top-1/2 
  before:-translate-y-1/2 before:-translate-x-full
  before:border-y-[16px] before:border-r-[16px] before:border-y-transparent 
  before:border-r-[#92D050]

  after:content-[''] after:absolute after:right-0 after:top-1/2 
  after:-translate-y-1/2 after:translate-x-full
  after:border-y-[16px] after:border-l-[16px] after:border-y-transparent 
  after:border-l-[#8CC54F]">
  AI Adoption Spectrum Across Respondents
</h1>


      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2">
      
        <div className="md:col-span-2 lg:col-span-3">
          <h4 className="text-black font-semibold mb-2 text-xl max-w-4xl text-center">
            Current AI Usage Patterns
          </h4>

          <div className="grid grid-cols-1 gap-2 lg:grid-cols-1">
            {Object.entries(aiPatterns).map(
              ([segment, patterns]:any) => (
                <div
                  key={segment}
                  className="border-[#9ADC54] border bg-[#FFF7CE] rounded-md p-3"
                >
                  <strong className="text-lg">{formatKey(segment)}</strong>
                  <ul className="list-disc pl-4 flex flex-col gap-1 text-black">
                    {patterns.map((item: string, index: number) => (
                      <li key={index}>{item}</li>
                    ))}
                  </ul>
                </div>
              )
            )}
    </div>
        </div>

        <div className="flex flex-col items-center">
          <h4 className="text-black font-semibold text-xl max-w-4xl text-center">
            AI Adoption Levels
          </h4>

          <div className="w-[280px] h-[340px] p-2">
            <Bar data={stackedData} options={stackedOptions} />
          </div>
        </div>
      </div>
    </div>
  );
}
